import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const SettingsContext = createContext({});

function hexToHSL(hex) {
  if (!hex || !hex.startsWith("#")) return null;
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function loadGoogleFont(family) {
  if (!family) return;
  const id = `gf-${family.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;500;600;700;800&display=swap`;
  document.head.appendChild(link);
}

function applyBranding(settings) {
  if (!settings) return;
  const root = document.documentElement;

  // Apply brand colors as CSS variables
  if (settings.brand_primary) {
    root.style.setProperty("--brand-primary", settings.brand_primary);
    const hsl = hexToHSL(settings.brand_primary);
    if (hsl) root.style.setProperty("--primary", hsl);
  }
  if (settings.brand_accent) {
    root.style.setProperty("--brand-accent", settings.brand_accent);
    const hsl = hexToHSL(settings.brand_accent);
    if (hsl) root.style.setProperty("--accent", hsl);
  }

  // Apply brand fonts
  const displayFont = settings.brand_font_display;
  const bodyFont = settings.brand_font_body;
  if (displayFont) {
    loadGoogleFont(displayFont);
    root.style.setProperty("--font-display", `'${displayFont}', Georgia, serif`);
  }
  if (bodyFont) {
    loadGoogleFont(bodyFont);
    root.style.setProperty("--font-body", `'${bodyFont}', system-ui, sans-serif`);
    document.body.style.fontFamily = `'${bodyFont}', system-ui, sans-serif`;
  }
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(null);

  const fetch = async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } catch (e) {
      setSettings({});
    }
  };

  useEffect(() => {
    fetch();
  }, []);

  // Apply branding whenever settings change
  useEffect(() => {
    if (settings) applyBranding(settings);
  }, [settings]);

  return (
    <SettingsContext.Provider value={{ settings: settings || {}, refresh: fetch }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  return useContext(SettingsContext);
}
